<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col sm="12">
            <h4 id="traffic" class="card-title mb-0">Destinatarios de Notificaciones</h4>
            <div class="small text-muted">Configurá qué staff y/o roles reciben cada tipo de evento (el evento y el canal los define el sistema)</div>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-group label="Módulo">
              <b-form-select v-model="filter.module_id" :options="moduleOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Canal">
              <b-form-select v-model="filter.channel" :options="arr.select.channels"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Implementación">
              <b-form-select v-model="filter.self_managed" :options="arr.select.self_managed"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Soporte">
              <b-form-select v-model="filter.deprecated" :options="arr.select.deprecated"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-table class="mb-0 notifications-event-types-table"
                  responsive="sm"
                  head-variant="dark"
                  :hover="true"
                  :items="filteredItems"
                  :fields="table.fields"
                  :tbody-tr-class="rowClass"
                  v-if="filteredItems.length">

              <template v-slot:cell(label)="data">
                <strong :class="{ 'text-danger': data.item.deprecated }">{{ data.item.label }}</strong>
                <b-badge variant="danger" class="ml-1" v-if="data.item.deprecated" v-b-tooltip.hover title="Este proceso está discontinuado, ya no tiene soporte">
                  <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>Sin soporte
                </b-badge>
                <b-badge variant="secondary" class="ml-1" v-if="data.item.self_managed" v-b-tooltip.hover title="Este evento ya envía notificaciones por su propia pantalla de configuración, no se gestiona desde acá">
                  <b-icon icon="gear-fill" class="mr-1"></b-icon>Implementación propia
                </b-badge>
                <b-badge variant="warning" class="ml-1" v-if="data.item.notifies_external" v-b-tooltip.hover title="También notifica a personas externas (clientes/proveedores), fuera del staff interno">
                  <b-icon icon="globe" class="mr-1"></b-icon>Externos
                </b-badge>
                <b-badge variant="info" class="ml-1" v-if="data.item.notifies_contextual" v-b-tooltip.hover title="También notifica a una persona puntual del proceso (quien lo generó, el responsable asignado, etc.), resuelta dinámicamente por el código, no configurable acá">
                  <b-icon icon="person-check" class="mr-1"></b-icon>Personas del proceso
                </b-badge>
                <div class="text-muted">{{ data.item.reference }}</div>
                <div class="text-muted" v-if="data.item.description">{{ data.item.description }}</div>
              </template>

              <template v-slot:cell(module)="data">
                <b-badge variant="info" v-if="data.item.module">
                  {{ moduleLabel(data.item.module) }}
                </b-badge>
                <span class="text-muted" v-else>Sistema</span>
              </template>

              <template v-slot:cell(channels)="data">
                <b-badge :variant="channelVariant(channel)" class="mr-1" v-for="channel in parseChannels(data.item.channels)" :key="channel">
                  {{ channelLabel(channel) }}
                </b-badge>
              </template>

              <template v-slot:cell(recipients)="data">
                <template v-if="data.item.self_managed">
                  <span class="text-muted">Se configura en su propia pantalla</span>
                </template>
                <template v-else-if="!countRecipients(data.item.staff_notification) && !countRecipients(data.item.roles_notification)">
                  <span class="text-muted">Sin configurar</span>
                </template>
                <div class="notifications-recipients-badges"
                     v-else
                     v-b-tooltip.hover.html
                     :title="recipientsTooltip(data.item)">
                  <b-badge pill variant="primary" v-if="countRecipients(data.item.roles_notification)">
                    <b-icon icon="people-fill" class="mr-1"></b-icon>{{ countRecipients(data.item.roles_notification) }} rol/es
                  </b-badge>
                  <b-badge pill variant="success" v-if="countRecipients(data.item.staff_notification)">
                    <b-icon icon="person-fill" class="mr-1"></b-icon>{{ countRecipients(data.item.staff_notification) }} staff
                  </b-badge>
                </div>
              </template>

              <template v-slot:cell(actions)="data">
                <div class="d-flex justify-content-center">
                  <b-dropdown size="sm"
                              variant="outline-dark"
                              toggle-class="notifications-event-types-actions-toggle"
                              right
                              no-caret>
                    <template v-slot:button-content>
                      <b-icon icon="three-dots-vertical"></b-icon>
                    </template>

                    <b-dropdown-item v-if="!data.item.self_managed" @click="editRecipients(data.item)">
                      <b-icon icon="pencil" class="mr-2"></b-icon>Destinatarios
                    </b-dropdown-item>
                    <b-dropdown-item v-if="!data.item.self_managed" @click="sendTest(data.item)">
                      <b-icon icon="arrow-up-circle-fill" class="mr-2"></b-icon>Enviar prueba
                    </b-dropdown-item>
                    <b-dropdown-item @click="viewLog(data.item)">
                      <b-icon icon="list-ul" class="mr-2"></b-icon>Ver notificaciones
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>

            </b-table>
            <b-alert v-else variant="warning" show>No se encontraron tipos de evento con esos filtros</b-alert>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <i class="fa fa-angle-double-left"></i>
              Volver
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <b-modal v-model="modal.form.active"
               header-bg-variant="dark"
               header-text-variant="white"
               size="lg"
               :key="modal.form._id"
               :id="modal.form._id">
        <div slot="modal-header">
          {{ modal.form.title }}
        </div>

        <EventTypeRecipients @select-recipients="getRecipientsData($event)"
                              :roles_notification="crud.form.roles_notification"
                              :staff_notification="crud.form.staff_notification" />

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()">Guardar</b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Storage from '@/handler/storageLocal'
  import EventTypeRecipients from './eventTypeRecipients'

  export default {
    components: {
      EventTypeRecipients
    },
    data: () => {
      return {
        access: {
          module_id: Modules.NOTIFICACIONES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'eventTypes',
          elements: {}
        },
        table: {
          items: [],
          fields: [
            {key: 'label', label: 'Tipo de evento', class: "align-middle"},
            {key: 'module', label: 'Módulo', class: "align-middle text-left"},
            {key: 'channels', label: 'Canal', class: "align-middle text-left"},
            {key: 'recipients', label: 'Destinatarios', class: "align-middle text-left"},
            {key: 'actions', label: '', class: "align-middle text-center"},
          ],
        },
        filter: {
          module_id: '',
          channel: '',
          self_managed: '',
          deprecated: '',
        },
        arr: {
          select: {
            channels: [
              { value: '', text: 'TODOS' },
              { value: 'internal', text: 'Interna' },
              { value: 'email', text: 'Email' },
              { value: 'sms', text: 'SMS' },
              { value: 'whatsapp', text: 'WhatsApp' },
              { value: 'push', text: 'Push' },
            ],
            self_managed: [
              { value: '', text: 'TODAS' },
              { value: '0', text: 'Configurada acá' },
              { value: '1', text: 'Implementación propia' },
            ],
            deprecated: [
              { value: '', text: 'TODAS' },
              { value: '0', text: 'Con soporte' },
              { value: '1', text: 'Sin soporte' },
            ],
          }
        },
        modal: {
          form: {
            active: false,
            _id: '',
            title: '',
          }
        },
        crud: {
          form: {
            id: null,
            roles_notification: [],
            staff_notification: [],
          }
        },
        // Nombres de roles/staff, para resolver el tooltip de "Destinatarios"
        // sin tener que abrir el modal de edición.
        names: {
          roles: {},
          staff: {},
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */

      this.restoreFilterStorage()
    },
    mounted() {
      this.search()
      this.loadNames()

      this._start = true
    },
    activated() {
      if (this._start) {
        this._start = false
        return
      }

      this.search()
      this.loadNames()
    },
    watch: {
      filter: {
        handler() {
          this.saveFilterStorage()
        },
        deep: true
      }
    },
    computed: {
      filteredItems() {
        return this.table.items.filter(item => {
          if (this.filter.module_id && item.module_id != this.filter.module_id) return false
          if (this.filter.channel && !this.parseChannels(item.channels).includes(this.filter.channel)) return false
          if (this.filter.self_managed !== '' && Boolean(item.self_managed) != Boolean(Number(this.filter.self_managed))) return false
          if (this.filter.deprecated !== '' && Boolean(item.deprecated) != Boolean(Number(this.filter.deprecated))) return false
          return true
        })
      },
      moduleOptions() {
        var seen = {}
        var options = [{ value: '', text: 'TODOS' }]

        this.table.items.forEach(item => {
          if (item.module && !seen[item.module_id]) {
            seen[item.module_id] = true
            options.push({ value: item.module_id, text: this.moduleLabel(item.module) })
          }
        });

        return options
      }
    },
    methods: {
      search() {
        let loader = this.$loading.show()

        serviceAPI.listEventTypes()
          .then((response) => {
            loader.hide()
            this.table.items = response.data
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      countRecipients(value) {
        if (!value) return 0
        return JSON.parse(value).length
      },
      loadNames() {
        serviceAPI.obtenerRoles().then(response => {
          response.data.forEach(role => {
            this.names.roles[role.id] = role.name
          })
        })

        serviceAPI.obtenerStaff().then(response => {
          response.data.forEach(staff => {
            this.names.staff[staff.id] = staff.name
          })
        })
      },
      recipientsTooltip(item) {
        var parts = []

        var roleIds = item.roles_notification ? JSON.parse(item.roles_notification) : []
        if (roleIds.length) {
          var roleNames = roleIds.map(id => this.names.roles[id] || ('#' + id))
          parts.push('<strong>Roles:</strong> ' + roleNames.join(', '))
        }

        var staffIds = item.staff_notification ? JSON.parse(item.staff_notification) : []
        if (staffIds.length) {
          var staffNames = staffIds.map(id => this.names.staff[id] || ('#' + id))
          parts.push('<strong>Staff:</strong> ' + staffNames.join(', '))
        }

        return parts.join('<br>')
      },
      moduleLabel(module) {
        var label = module.title || module.name
        return label.substring(label.indexOf('.') + 1)
      },
      parseChannels(value) {
        if (!value) return ['internal']
        try {
          return JSON.parse(value)
        } catch (e) {
          return ['internal']
        }
      },
      channelLabel(channel) {
        var labels = { internal: 'Interna', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push' }
        return labels[channel] || channel
      },
      channelVariant(channel) {
        var variants = { internal: 'info', email: 'primary', sms: 'secondary', whatsapp: 'success', push: 'dark' }
        return variants[channel] || 'secondary'
      },
      rowClass(item) {
        if (!item) return ''
        if (item.deprecated) return 'notifications-event-types-row-deprecated'
        return ''
      },
      saveFilterStorage() {
        Storage.setValue('filter_view_notifications_event_types', JSON.stringify(this.filter))
      },
      restoreFilterStorage() {
        if (Storage.getValue('filter_view_notifications_event_types')) {
          Object.assign(this.filter, JSON.parse(Storage.getValue('filter_view_notifications_event_types')))
        }
      },
      editRecipients(item) {
        this.crud.form.id = item.id
        this.crud.form.roles_notification = item.roles_notification ? JSON.parse(item.roles_notification) : []
        this.crud.form.staff_notification = item.staff_notification ? JSON.parse(item.staff_notification) : []

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Destinatarios: " + item.label
        this.modal.form.active = true
      },
      getRecipientsData(data) {
        var roles = []
        if (data.roles_notification) {
          data.roles_notification.forEach(element => {
            roles.push(element.code)
          });
        }
        this.crud.form.roles_notification = roles

        var staff = []
        if (data.staff_notification) {
          data.staff_notification.forEach(element => {
            staff.push(element.code)
          });
        }
        this.crud.form.staff_notification = staff
      },
      save() {
        let loader = this.$loading.show()

        serviceAPI.updateEventTypeRecipients(this.crud.form.id, {
          roles_notification: this.crud.form.roles_notification,
          staff_notification: this.crud.form.staff_notification,
        })
        .then(() => {
          loader.hide()
          this.modal.form.active = false
          this.$awn.success("Destinatarios actualizados")
          this.search()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      viewLog(item) {
        // Los procesos "implementación propia" no siempre loguean con el mismo
        // reference que usamos acá para catalogarlos (muchos loguean con
        // reference vacío) — para esos solo filtramos por módulo, para no
        // mostrar "0 resultados" con un filtro que nunca va a matchear.
        var query = { module_id: item.module_id }
        if (!item.self_managed) {
          query.reference = item.reference
        }
        this.$router.push({ name: 'NotificationsStaffLog', query: query })
      },
      sendTest(item) {
        serviceAPI.sendEventTypeTest(item.id)
          .then((response) => {
            var result = response.data
            var successCount = result.filter(r => r.status).length

            if (successCount) {
              this.$awn.success("Notificación de prueba enviada a " + successCount + " destinatario(s)")
            } else {
              var error = (result[0] && result[0].error) ? result[0].error : "No se pudo enviar la notificación de prueba"
              this.$awn.alert(error)
            }
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
    }
  }
</script>
<style scoped>
  .notifications-event-types-table {
    font-size: 1rem;
  }
  .notifications-event-types-table .badge {
    font-size: 0.85rem;
  }
  .notifications-recipients-badges {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    cursor: help;
  }
  .notifications-recipients-badges .badge {
    padding: 0.4em 0.7em;
  }
</style>
<style>
  .notifications-event-types-row-deprecated {
    box-shadow: inset 3px 0 0 0 #dc3545;
    background-color: #fdecea;
  }
  .notifications-event-types-actions-toggle {
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
</style>
